import Paper from '@/components/Paper'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import DashboardTimeline from '@/modules/Oncall/components/Assignments/Timeline/components/DashboardTimeline'
import useAuthStore from '@/stores/useAuthStore'
import dayjs from 'dayjs'
import { Mail, Phone, User } from 'lucide-react'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { Tooltip } from 'vnetwork-ui'
import { getOncallByDepartment, OncallAssignment } from '../../../services'

import { DEPARTMENT_IDS } from '@/constants/departments'

const getDiceBearAvatar = (seed: string) =>
    `https://api.dicebear.com/9.x/thumbs/svg?seed=${encodeURIComponent(seed)}`

const SalesOncallSchedule: React.FC = () => {
    // const { t } = useTranslation()
    const today = dayjs().format('YYYY-MM-DD')
    const currentEmployee = useAuthStore((state) => state.employee)

    const { data } = useQueryWithHandler({
        queryKey: [QUERY_KEYS.MARKETING, 'sales-oncall-today', today],
        queryFn: () =>
            getOncallByDepartment({
                department_id: DEPARTMENT_IDS.SALES,
                start_date: today,
                end_date: today,
            }),
    })

    const todayAssignment = data?.results?.[0]
    const isCurrentUserOncall =
        todayAssignment?.employee_id === currentEmployee?.employee_id

    return (
        <Paper
            className="col-span-12 lg:col-span-6 !p-0 h-full space-y-2"
            data-tour="sales-oncall"
        >
            <div className="p-4 pb-0">
                <DashboardTimeline departmentId={DEPARTMENT_IDS.SALES} />
            </div>
            <div className="px-4 pb-4">
                <WhosOncall
                    assignment={todayAssignment}
                    isCurrentUser={isCurrentUserOncall}
                />
            </div>
        </Paper>
    )
}

const WhosOncall: React.FC<{
    assignment?: OncallAssignment
    isCurrentUser?: boolean
}> = ({ assignment, isCurrentUser }) => {
    const { t } = useTranslation()

    if (!assignment) {
        return (
            <div className="bg-gray-50 rounded-sm p-2 flex items-center justify-center">
                <span className="text-xs text-gray-400 italic">
                    {t('no-oncall-today')}
                </span>
            </div>
        )
    }

    return (
        <div
            className={`rounded-sm p-4 ${isCurrentUser ? 'bg-amber-50 border border-amber-300' : 'bg-gray-50'}`}
        >
            <div className="flex items-center gap-1.5 mb-2">
                <User
                    size={12}
                    className={
                        isCurrentUser ? 'text-amber-600' : 'text-primary'
                    }
                />
                <span
                    className={`text-xs font-medium ${isCurrentUser ? 'text-amber-600' : 'text-primary'}`}
                >
                    {t('whos-oncall-today')}
                </span>
                {isCurrentUser && (
                    <span className="ml-auto bg-amber-400 text-amber-900 text-[10px] font-semibold px-1.5 py-0.5 rounded">
                        {t('its-you')}
                    </span>
                )}
            </div>
            <div className="flex items-center gap-2">
                <img
                    src={getDiceBearAvatar(
                        assignment.employee_email_company ||
                            assignment.employee_name
                    )}
                    alt={assignment.employee_name}
                    className={`w-10 h-10 rounded-full border-2 flex-shrink-0 ${isCurrentUser ? 'border-amber-500' : 'border-green-500'}`}
                />
                <div className="min-w-0 flex-1">
                    <div
                        className={`font-medium text-sm truncate ${isCurrentUser ? 'text-amber-800' : 'text-gray-800'}`}
                    >
                        {assignment.employee_name}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                        {assignment.employee_phone_number && (
                            <Tooltip title={assignment.employee_phone_number}>
                                <a
                                    href={`tel:${assignment.employee_phone_number}`}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <Phone size={12} />
                                </a>
                            </Tooltip>
                        )}
                        {assignment.employee_email_company && (
                            <Tooltip title={assignment.employee_email_company}>
                                <a
                                    href={`mailto:${assignment.employee_email_company}`}
                                    className="text-blue-600 hover:text-blue-800"
                                >
                                    <Mail size={12} />
                                </a>
                            </Tooltip>
                        )}
                    </div>
                </div>
            </div>
        </div>
    )
}

export default SalesOncallSchedule
